/**
 * Turns the stored flat set of nodes into the tree the interface shows.
 *
 * Deliberately a pure function with its own tests: this is where the silent
 * mistakes happen - a child whose parent was deleted disappears from the view,
 * and a cycle hangs the renderer.
 */
export function buildLocationTree(nodes) {
    const byId = new Map();
    for (const node of nodes) {
        byId.set(node.id, { node, children: [], orphaned: false });
    }
    const roots = [];
    for (const entry of byId.values()) {
        const parentId = entry.node.parentLocationId;
        if (parentId === undefined) {
            roots.push(entry);
            continue;
        }
        const parent = byId.get(parentId);
        if (parent === undefined || isDescendantOfSelf(entry.node, byId)) {
            entry.orphaned = true;
            roots.push(entry);
            continue;
        }
        parent.children.push(entry);
    }
    sortRecursively(roots);
    return roots;
}
/**
 * Walks up the parent chain. A node that reaches itself is part of a cycle and
 * must not be nested, or rendering never terminates. The map size bounds the
 * walk, so a corrupted chain cannot spin either.
 */
function isDescendantOfSelf(node, byId) {
    let currentId = node.parentLocationId;
    for (let steps = 0; currentId !== undefined && steps <= byId.size; steps += 1) {
        if (currentId === node.id)
            return true;
        currentId = byId.get(currentId)?.node.parentLocationId;
    }
    return false;
}
function sortRecursively(entries) {
    entries.sort((a, b) => {
        if (a.node.order !== b.node.order)
            return a.node.order - b.node.order;
        return (a.node.name ?? a.node.code ?? '').localeCompare(b.node.name ?? b.node.code ?? '', 'de');
    });
    for (const entry of entries) {
        sortRecursively(entry.children);
    }
}
//# sourceMappingURL=location-tree.js.map